/* Advanced course - "Sledeći korak".

   Lives on a different slug in each language (/napredni-kurs-sminkanja,
   /en/advanced-makeup-course, /de/fortgeschrittenen-kurs); the mapping is in
   ROUTE_SLUGS in src/i18n/lang.mjs and everything else keys off the logical
   Serbian route.

   The price comes from ADVANCED_COURSE: while showPrice is true the
   practical-info table prints it with the deposit, and flipping the flag back
   restores the "on request" wording and drops `offers` from the JSON-LD. */

import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Bredcrumb from '../../HomeMain/Bredcrumb';
import Faq from '../../ui/Faq';
import CourseSignupForm from '../../Educations/CourseSignupForm';
import Testimonials from '../../Educations/Testimonials';
import {
  Section, Heading, P, NumberedList, FeatureList, InfoTable,
  buttonStyle, interpolate, BRAND, INK, sans,
} from '../../ui/blocks';
import OffersLink from '../../ui/OffersLink';
import { ADVANCED_COURSE } from '../../../config/educations.mjs';
import { linkTo } from '../../../i18n/lang.mjs';

const CourseImg = 'https://res.cloudinary.com/dxa2jevmo/image/upload/v1761861297/IMG_4238_n36sci.jpg';

const BASIC = '/educations';

const Main = () => {
  const { t, i18n } = useTranslation();
  const lang = ['sr', 'en', 'de'].includes(i18n.language) ? i18n.language : 'sr';
  const basicHref = linkTo(BASIC, lang);

  const mentoring = t('advancedCourse.mentoring.items', { returnObjects: true });
  const camera = t('advancedCourse.camera.items', { returnObjects: true });
  const technical = t('advancedCourse.technical.items', { returnObjects: true });
  const included = t('advancedCourse.included.items', { returnObjects: true });
  const faq = t('advancedCourse.faq.items', { returnObjects: true });

  /* The last row is the price. While the price is private it stays as the
     "on request" wording that came with the copy; flipping showPrice swaps in
     the amount without touching this component. */
  const rows = t('advancedCourse.practical.rows', { returnObjects: true }).map((row) => ({ ...row }));
  const practicalRows = ADVANCED_COURSE.showPrice
    ? [
      ...rows.slice(0, -1),
      {
        label: t('advancedCourse.practical.priceRowLabel'),
        value: interpolate(t('advancedCourse.practical.priceRowValue'), {
          price: ADVANCED_COURSE.priceEur,
          deposit: ADVANCED_COURSE.depositEur,
        }),
      },
    ]
    : rows;

  return (
    <>
      <Bredcrumb title={t('pages.advancedCourse.title')} subtitle={t('pages.advancedCourse.subtitle')} />

      <Section>
        <P style={{ fontSize: '18px', color: INK, fontWeight: 500 }}>{t('advancedCourse.lead.p1')}</P>
        <P>{t('advancedCourse.lead.p2')}</P>
        <P style={{ fontWeight: 600, color: INK }}>{t('advancedCourse.lead.p3')}</P>
        <P>{t('advancedCourse.lead.p4')}</P>
        <P style={{ marginBottom: 0 }}>{t('advancedCourse.lead.p5')}</P>
      </Section>

      <Section tinted>
        <Heading>{t('advancedCourse.howItWorks.heading')}</Heading>
        <P>{t('advancedCourse.howItWorks.p1')}</P>
        <P>{t('advancedCourse.howItWorks.p2')}</P>
        <P>{t('advancedCourse.howItWorks.p3')}</P>
        <P style={{ marginBottom: 0 }}>{t('advancedCourse.howItWorks.p4')}</P>
      </Section>

      <Section>
        <Heading>{t('advancedCourse.mentoring.heading')}</Heading>
        <P>{t('advancedCourse.mentoring.p1')}</P>
        <P>{t('advancedCourse.mentoring.p2')}</P>
        <div style={{ marginTop: '24px' }}>
          <FeatureList items={mentoring} />
        </div>
        <P style={{ marginTop: '24px', marginBottom: 0, fontWeight: 600, color: INK }}>
          {t('advancedCourse.mentoring.closing')}
        </P>
      </Section>

      <Section tinted>
        <Heading>{t('advancedCourse.camera.heading')}</Heading>
        <P>{t('advancedCourse.camera.p1')}</P>
        <P>{t('advancedCourse.camera.p2')}</P>
        <div style={{ marginTop: '24px' }}>
          <FeatureList items={camera} />
        </div>
        <P style={{ marginTop: '24px', marginBottom: 0, fontWeight: 600, color: INK }}>
          {t('advancedCourse.camera.closing')}
        </P>
      </Section>

      <Section>
        <Heading>{t('advancedCourse.pricingPlan.heading')}</Heading>
        <P style={{ fontSize: '18px', color: INK, fontWeight: 600 }}>{t('advancedCourse.pricingPlan.p1')}</P>
        <P style={{ marginBottom: 0 }}>{t('advancedCourse.pricingPlan.p2')}</P>

        <img
          src={CourseImg}
          alt={t('pages.advancedCourse.title')}
          loading="lazy"
          width="2232"
          height="3210"
          style={{ display: 'block', width: '100%', height: 'auto', borderRadius: '20px', marginTop: '32px' }}
        />
      </Section>

      <Section tinted>
        <Heading>{t('advancedCourse.technical.heading')}</Heading>
        <P>{t('advancedCourse.technical.intro')}</P>
        <NumberedList items={technical} />
      </Section>

      <Section>
        <Heading>{t('advancedCourse.included.heading')}</Heading>
        <FeatureList items={included} newBadge={t('advancedCourse.included.newBadge')} />
      </Section>

      <Section tinted>
        <Heading>{t('advancedCourse.whyMe.heading')}</Heading>
        <P>{t('advancedCourse.whyMe.p1')}</P>
        <P>{t('advancedCourse.whyMe.p2')}</P>
        <P>{t('advancedCourse.whyMe.p3')}</P>
        <P style={{ marginBottom: 0, fontWeight: 600, color: INK }}>{t('advancedCourse.whyMe.p4')}</P>
      </Section>

      {/* Renders nothing at all until TESTIMONIALS has an 'advanced' entry. */}
      <Testimonials kurs="advanced" headingKey="advancedCourse.testimonials.heading" />

      <Section>
        <Heading>{t('advancedCourse.practical.heading')}</Heading>
        <InfoTable rows={practicalRows} />
      </Section>

      <Section tinted>
        <Heading align="center">{t('advancedCourse.faq.heading')}</Heading>
        <Faq
          items={faq}
          renderExtra={(item) => (item.linkText ? (
            <Link
              to={basicHref}
              style={{
                display: 'inline-block',
                marginTop: '14px',
                fontFamily: sans,
                fontSize: '15px',
                fontWeight: 600,
                color: BRAND,
              }}
            >
              {item.linkText} →
            </Link>
          ) : null)}
        />
      </Section>

      <CourseSignupForm defaultCourse="advanced" />

      <Section>
        <div style={{ textAlign: 'center' }}>
          <Link to={basicHref} style={buttonStyle('outline')}>
            {t('advancedCourse.backLink')}
          </Link>
          <div style={{ marginTop: '26px' }}>
            <OffersLink />
          </div>
        </div>
      </Section>
    </>
  );
};

export default Main;
